import { api } from "aldoo-ra/API"

const permissionsAPI = api("permissions")

export const PermissionAPI = {
  /**
   * Check if user has specific permissions
   * @param {string} userId - User ID
   * @param {string|Array<string>} permissions - Permission(s) to check
   * @returns {Promise<boolean>}
   */
  async hasPermission(userId, permissions) {
    if (!userId) {
      return false
    }
    
    const permissionArray = Array.isArray(permissions) ? permissions : [permissions]
    
    try {
      const response = await permissionsAPI({
        has_permission: {
          user: userId,
          permission: permissionArray
        }
      })
      
      return response.result === true
    } catch (error) {
      console.error("PermissionAPI: Permission check failed:", error)
      return false
    }
  },

  /**
   * Get all permissions for a user
   * @param {string} userId - User ID
   * @returns {Promise<Array<string>>}
   */
  async getUserPermissions(userId) {
    if (!userId) return []
    
    try {
      const response = await permissionsAPI({
        get_permission: {
          user: userId
        }
      })
      // Tolerate both response shapes: a bare array or { permissions: [...] }
      return Array.isArray(response) ? response : (response?.permissions || [])
    } catch (error) {
      console.error("Failed to get user permissions:", error)
      return []
    }
  },

  /**
   * Grant permissions to a user
   * @param {string} userId - User ID
   * @param {Array<string>} permissions - Permissions to grant
   * @returns {Promise<{result: boolean, error?: string}>}
   */
  async grantPermissions(userId, permissions) {
    try {
      const response = await permissionsAPI({
        grant_permission: {
          user: userId,
          permission: permissions
        }
      })
      return {
        result: response.result,
        error: response.error
      }
    } catch (error) {
      console.error("Failed to grant permissions:", error)
      return {
        result: false,
        error: error.message || "Failed to grant permissions"
      }
    }
  },

  /**
   * Revoke permissions from a user
   * @param {string} userId - User ID
   * @param {Array<string>} permissions - Permissions to revoke
   * @returns {Promise<{result: boolean, error?: string}>}
   */
  async revokePermissions(userId, permissions) {
    try {
      const response = await permissionsAPI({
        revoke_permission: {
          user: userId,
          permission: permissions
        }
      })
      return {
        result: response.result,
        error: response.error
      }
    } catch (error) {
      console.error("Failed to revoke permissions:", error)
      return {
        result: false,
        error: error.message || "Failed to revoke permissions"
      }
    }
  }
}